import { useRouter } from 'next/router';
import {
  Button,
  Container,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { MetaHead } from 'components/MetaHead/MetaHead';
import { HeaderWithOptions } from 'components/Header';
import { Footer } from 'components/Footer';
import { GreenHouse, JobAdvantages, Headquarter, Teams } from 'components/Jobs';
import { DATENSCHUTZ_LINK_JOBS } from 'constants/pageLinks';

const StyledList = styled(List)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: theme.spacing(4),
}));


export default function JobsPage() {
  const router = useRouter();
  const hideOtherPageContent = router.query?.gh_jid ? true : false;

  return (
    <>
      <MetaHead
        title="Deine Karriere bei smava » Informiere dich jetzt! | SMAVA"
        description="smava.de stellt sich vor: Unsere Stellenangebote und Unternehmensprofil. Wir freuen uns, Dich kennenzulernen!"
        keywords="karriere,jobs,smava,stellenanzeigen,stellenangebote,fintech,berlin"
        ogTitle="Deine Karriere bei smava » Informiere dich jetzt! | SMAVA"
        ogDescription="smava.de stellt sich vor: Unsere Stellenangebote und Unternehmensprofil. Wir freuen uns, Dich kennenzulernen!"
        follow={true}
        index={true}
      />

      <HeaderWithOptions
        hasLoginLink={false}
        hasNavigationMenu={true}
        hasPhoneBox={false}
      />

      <GreenHouse showAnimation={true} />

      {!hideOtherPageContent && (
        <>
          <Container
            maxWidth="md"
            style={{ marginTop: '2rem', marginBottom: '3rem' }}
          >
            <Typography variant="h1" component="h2">
              Lass uns gemeinsam Kredite transparent, fair und günstig machen
            </Typography>

            <Typography variant="body1">
              Wir glauben, dass Märkte transparent und fair sein sollten. Der
              Kreditmarkt in Deutschland ist nichts von beidem. Deshalb zahlen
              die Deutschen jedes Jahr Milliarden Euro zu viel für ihre Kredite.
              Das wollen wir verhindern. Dazu kombinieren wir seit 2007 Finanz-
              mit Technologie-Know-how. Das Ergebnis sind Produkte wie unser
              Kreditvergleichsrechner, Deutschlands erster P2P-Kredit,
              Deutschlands erster Digital-Kredit und Deutschlands erster Kredit
              mit Negativzins. Mit ihnen machen wir Kredite für
              Verbraucher*innen transparent, fair und günstig. Diese Mission
              unterstützen auch unsere renommierten Investitionsgesellschaften
              wie Vitruvian Partners, Verdane Capital, Runa Capital und
              Earlybird. Mit insgesamt 159 Millionen US-Dollar Investment gehört
              smava zu den am besten finanzierten Fintechs in Deutschland.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              href="/ueber-smava/"
              style={{ marginTop: 0 }}
            >
              Erfahre mehr über smava
            </Button>
          </Container>

          <Container maxWidth="md">
            <Typography variant="h2" component="h2">
              Bei uns zählst du!
            </Typography>
            <Typography variant="body1" component="p">
              Unsere Arbeitsweise ist hands-on und wir streben stets danach,
              unsere Prozesse so agil wie möglich zu gestalten. Dabei ist unser
              Motto &quot;People over processes&quot;. Denn bei uns werden
              flache Hierarchien gelebt und viel Wert auf eine offene, direkte
              Kommunikation sowie auf eine Fehler- und Feedback-Kultur gelegt.
              Bei uns ist alles etwas schneller und dynamischer. Werde auch du
              Teil einer bunt gemischten Crew aus rund 50 verschiedenen
              Nationalitäten! Ein schönes Arbeitsumfeld mit Kolleg*innen, denen
              du vertrauen kannst und die dich positiv herausfordern, um dein
              Potential voll auszuschöpfen. Auf dieses smava Ökosystem sind wir
              besonders stolz. Wir investieren in die Zufriedenheit aller Teams
              sowie aller Mitarbeitenden. Für mehr Einblicke hinter den Kulissen
              folge uns auch gern auf Social Media! Die Links zu unseren
              Accounts findest du in der Fußzeile der Seite.
            </Typography>

            <Typography variant="h2" component="h2">
              Unsere Arbeitsprinzipien
            </Typography>
            <StyledList disablePadding>
              {[
                'Customers first! Bei smava kommen die Kund*innen zuerst.',
                'Play to win! Gib alles, um smava zum größten Online-Anbieter für Kredite zu machen.',
                'Focus on impact! Konzentriere dich bei allen Projekten von Anfang an auf das Ergebnis, das du erzielen willst.',
                'Be bold! Triff mutige Entscheidungen und hab keine Angst vor',
                'Move fast! Sei schnell und nutze jede Chance sofort.',
              ].map((item: string, idx: number) => {
                return (
                  <ListItem
                    disablePadding
                    disableGutters
                    key={`principles_list_${idx}`}
                  >
                    <ListItemIcon>
                      <ArrowForwardIcon />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ marginBottom: 0 }}
                      primary={item}
                    ></ListItemText>
                  </ListItem>
                );
              })}
            </StyledList>
          </Container>
          <JobAdvantages />

          <Headquarter />

          <Container maxWidth="md" style={{ marginBottom: '4rem' }}>
            <Typography variant="h2" component="h3">
              Unsere Teams
            </Typography>
            <Typography variant="body1">
              Gemeinsam mit mehr als 600 Mitarbeiter*innen aus rund 40
              verschiedenen Nationen arbeiten wir täglich daran, smava
              kontinuierlich besser zu machen. Stets begleiten uns dabei unsere
              wichtigsten Arbeitsprinzipien: Customers First, Play to Win, Be
              Bold, Focus on Impact und Move Fast. Finde jetzt das Team, das zu
              dir passt!
            </Typography>
          </Container>

          <Teams />
        </>
      )}

      <Footer datenschutzUrl={DATENSCHUTZ_LINK_JOBS} />
    </>
  );
}
